import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  PlusIcon,
  TrashIcon,
  XMarkIcon,
  ArrowLeftIcon,
  CheckIcon,
  PencilIcon,
  EyeIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";
import {
  fetchMeetingMinutesMeeting,
  createMeetingMinutesGroup,
  updateMeetingMinutesGroup,
  deleteMeetingMinutesGroup,
  createMeetingMinutesEntry,
  updateMeetingMinutesEntry,
  deleteMeetingMinutesEntry,
  fetchMeetingMinutesTemplates,
  fetchMeetingMinutesById,
  updateMeetingMinutesMeeting,
} from "../../../api/meetingMinutes";
import { fetchUsers } from "../../../api/user";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../components/utils/ResourceError";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { PMCustomSelector } from "../../../components/utils/PMCustomSelector";
import { SaveBar } from "../../../components/utils/SaveBar";
import { DocumentManagerV6 } from "../../../components/dashboard/Media/DocumentManagerV6";
import { PMDialog } from "../../../components/utils/PMDialog";

export const MeetingMinutesMeetingView = () => {
  const { t } = useTranslation();
  const { id, meetingId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [meeting, setMeeting] = useState(null);
  const [meetingMinutes, setMeetingMinutes] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'saving', 'success', 'error'
  const [saveErrorMessage, setSaveErrorMessage] = useState(null);
  const [documentRefreshTrigger, setDocumentRefreshTrigger] = useState(0);
  const [isEditMode, setIsEditMode] = useState(true); // Default to edit mode - can be toggled to view mode
  const [previousMeeting, setPreviousMeeting] = useState(null);
  const [isPreviousMeeting, setIsPreviousMeeting] = useState(false); // True if there's a newer meeting
  const [expandedGroups, setExpandedGroups] = useState({}); // Track which groups in previous meeting are expanded
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null); // null for new, group object for editing
  const [groupFormData, setGroupFormData] = useState({ title: "", description: "" });
  const debounceTimers = useRef({});
  const meetingRef = useRef(null);
  const initialMeetingRef = useRef(null);
  const scrollPositionRef = useRef(null);

  // Stable ref for DocumentManagerV6: inline callbacks break its loadDocuments deps and can retrigger autoLoad forever when the list is empty.
  const handleEntryDocumentsChange = useCallback(() => {
    setDocumentRefreshTrigger((prev) => prev + 1);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
      const meetingData = await fetchMeetingMinutesMeeting(id, meetingId);
      setMeeting(meetingData);
      meetingRef.current = meetingData;
      initialMeetingRef.current = JSON.parse(JSON.stringify(meetingData)); // Deep copy for comparison
      setIsFinished(meetingData.is_finished === true);
      
      // If meeting is finished, default to view mode, otherwise edit mode
      if (meetingData.is_finished === true) {
        setIsEditMode(false);
      }
      
        const minutesData = await fetchMeetingMinutesById(id);
        setMeetingMinutes(minutesData);
        
        // Check if there's a previous meeting (meeting_number - 1)
        if (meetingData.meeting_number && meetingData.meeting_number > 1) {
          try {
            // Find the previous meeting by meeting_number
            const previousMeetingNumber = meetingData.meeting_number - 1;
            const previousMeetingData = minutesData.meetings?.find(m => m.meeting_number === previousMeetingNumber);
            if (previousMeetingData) {
              const prevMeeting = await fetchMeetingMinutesMeeting(id, previousMeetingData.id);
              setPreviousMeeting(prevMeeting);
            }
          } catch (err) {
            console.error("Error fetching previous meeting:", err);
          }
        }
        
        // Check if this is a previous meeting (there's a newer meeting with higher meeting_number)
        // Only entries should be read-only for previous meetings, not the entire meeting
        // Groups can still be edited in previous meetings (unless finished)
        if (minutesData.meetings && minutesData.meetings.length > 0 && meetingData.meeting_number) {
          const maxMeetingNumber = Math.max(...minutesData.meetings.map(m => m.meeting_number || 0));
          // Only consider it a previous meeting if there's a newer meeting with higher number
          // This is used to make entries read-only, but groups can still be edited
          const hasNewerMeeting = maxMeetingNumber > meetingData.meeting_number;
          setIsPreviousMeeting(hasNewerMeeting);
        } else {
          // If no meetings data or no meeting_number, definitely not a previous meeting
          setIsPreviousMeeting(false);
        }
        
        // Check if meeting minutes is finished - if so, prevent finishing meetings
        // Note: This assumes meeting minutes has an is_finished field
        // If not, we'll need to add this check based on your business logic
      } catch (err) {
        console.error("Error fetching meeting:", err);
        setError(true);
        toast.error(t("meeting_minutes.meeting.load_error"));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, meetingId]);

  // Cleanup debounce timers on unmount
  useEffect(() => {
    return () => {
      Object.values(debounceTimers.current).forEach((timer) => {
        if (timer) clearTimeout(timer);
      });
    };
  }, []);

  const openGroupDialog = (group = null) => {
    if (group) {
      setEditingGroup(group);
      setGroupFormData({
        title: group.title || "",
        description: group.description || "",
      });
    } else {
      setEditingGroup(null);
      setGroupFormData({ title: "", description: "" });
    }
    setGroupDialogOpen(true);
  };

  const closeGroupDialog = () => {
    setGroupDialogOpen(false);
    setEditingGroup(null);
    setGroupFormData({ title: "", description: "" });
  };

  const saveGroup = async () => {
    try {
      setSaving(true);
      if (editingGroup) {
        // Update existing group
        const updatedGroup = await updateMeetingMinutesGroup(id, meetingId, editingGroup.id, {
          ...editingGroup,
          title: groupFormData.title,
          description: groupFormData.description,
        });
        
        setMeeting((prevMeeting) => {
          const updated = {
            ...prevMeeting,
            groups: prevMeeting.groups.map((g) =>
              g.id === editingGroup.id ? updatedGroup : g
            ),
          };
          meetingRef.current = updated;
          return updated;
        });
        toast.success(t("meeting_minutes.group.updated") || "Group updated");
      } else {
        // Create new group
        const currentMeeting = meetingRef.current || meeting;
        const newGroup = await createMeetingMinutesGroup(id, meetingId, {
          title: groupFormData.title,
          description: groupFormData.description,
          order: currentMeeting?.groups?.length || 0,
        });
        
        setMeeting((prevMeeting) => {
          const updated = {
            ...prevMeeting,
            groups: [...(prevMeeting.groups || []), newGroup],
          };
          meetingRef.current = updated;
          
          // Scroll to the new group after a short delay
          setTimeout(() => {
            const element = document.querySelector(`[data-group-id="${newGroup.id}"]`);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
          
          return updated;
        });
        toast.success(t("meeting_minutes.group.created") || "Group created");
      }
      closeGroupDialog();
    } catch (err) {
      console.error("Error saving group:", err);
      toast.error(editingGroup ? t("meeting_minutes.point.update_error") : t("meeting_minutes.point.create_error"));
    } finally {
      setSaving(false);
    }
  };

  const addGroup = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    openGroupDialog();
  };

  // Update local state immediately for responsive UI
  const updateGroupLocal = (groupId, field, value) => {
    setMeeting((prevMeeting) => {
      const updated = {
        ...prevMeeting,
        groups: prevMeeting.groups.map((g) =>
          g.id === groupId ? { ...g, [field]: value } : g
        ),
      };
      meetingRef.current = updated;
      checkForUnsavedChanges(updated);
      return updated;
    });
  };

  // Check if there are unsaved changes
  const checkForUnsavedChanges = (currentMeeting) => {
    if (!initialMeetingRef.current) return;
    const hasChanges = JSON.stringify(currentMeeting) !== JSON.stringify(initialMeetingRef.current);
    setHasUnsavedChanges(hasChanges);
  };

  // Debounced API update
  const updateGroup = useCallback((groupId, field, value) => {
    // Update local state immediately
    updateGroupLocal(groupId, field, value);

    // Clear existing timer for this group+field combination
    const timerKey = `group-${groupId}-${field}`;
    const existingTimer = debounceTimers.current[timerKey];
    if (existingTimer) {
      clearTimeout(existingTimer);
      debounceTimers.current[timerKey] = null;
    }

    // Set new timer
    const timer = setTimeout(async () => {
      try {
        setSaveStatus('saving');
        const currentMeeting = meetingRef.current || { groups: [] };
        const group = currentMeeting.groups.find((g) => g.id === groupId);
        if (!group) {
          setSaveStatus(null);
          delete debounceTimers.current[timerKey];
          return;
        }

        const updatedGroup = await updateMeetingMinutesGroup(id, meetingId, groupId, {
          ...group,
          [field]: value,
        });

        setMeeting((prevMeeting) => {
          const updated = {
            ...prevMeeting,
            groups: prevMeeting.groups.map((g) =>
              g.id === groupId ? updatedGroup : g
            ),
          };
          meetingRef.current = updated;
          initialMeetingRef.current = JSON.parse(JSON.stringify(updated)); // Update initial state
          setHasUnsavedChanges(false);
          setSaveStatus('success');
          setTimeout(() => setSaveStatus(null), 3000);
          return updated;
        });
      } catch (err) {
        console.error("Error updating group:", err);
        setSaveStatus('error');
        setSaveErrorMessage(t("meeting_minutes.group.update_error"));
        setTimeout(() => {
          setSaveStatus(null);
          setSaveErrorMessage(null);
        }, 5000);
        toast.error(t("meeting_minutes.group.update_error"));
      } finally {
        delete debounceTimers.current[timerKey];
      }
    }, 500); // 500ms debounce
    
    // Store timer reference
    debounceTimers.current[timerKey] = timer;
  }, [id, meetingId, t]);

  const removeGroup = async (groupId) => {
    if (!window.confirm(t("meeting_minutes.group.delete_confirm"))) {
      return;
    }

    try {
      await deleteMeetingMinutesGroup(id, meetingId, groupId);
      setMeeting((prevMeeting) => {
        const updated = {
          ...prevMeeting,
          groups: prevMeeting.groups.filter(g => g.id !== groupId),
        };
        meetingRef.current = updated;
        return updated;
      });
      toast.success(t("meeting_minutes.group.deleted"));
    } catch (err) {
      console.error("Error deleting group:", err);
      toast.error(t("meeting_minutes.group.delete_error"));
    }
  };

  const addEntry = async (e, groupId) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Store current scroll position to prevent auto-scroll
    scrollPositionRef.current = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    
    try {
      setSaving(true);
      const currentMeeting = meetingRef.current || meeting;
      const group = currentMeeting?.groups?.find(g => g.id === groupId);
      const newEntry = await createMeetingMinutesEntry(id, meetingId, groupId, {
        description: "",
        deadline: "",
        responsible_user_id: null,
        order: group?.entries?.length || 0,
      });
      
      setMeeting((prevMeeting) => {
        const updated = {
          ...prevMeeting,
          groups: prevMeeting.groups.map(g => 
            g.id === groupId 
              ? { ...g, entries: [...(g.entries || []), newEntry] }
              : g
          ),
        };
        meetingRef.current = updated;
        return updated;
      });
    } catch (err) {
      console.error("Error creating entry:", err);
      toast.error(t("meeting_minutes.entry.create_error"));
      scrollPositionRef.current = null; // Clear on error
    } finally {
      setSaving(false);
    }
  };

  // Restore scroll position after DOM updates
  useEffect(() => {
    if (scrollPositionRef.current !== null) {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const scrollPos = scrollPositionRef.current;
          if (scrollPos !== null) {
            window.scrollTo({
              top: scrollPos,
              left: 0,
              behavior: 'instant'
            });
            // Also set directly as fallback
            if (document.documentElement) {
              document.documentElement.scrollTop = scrollPos;
            }
            if (document.body) {
              document.body.scrollTop = scrollPos;
            }
            scrollPositionRef.current = null; // Clear after restoring
          }
        });
      });
    }
  }, [meeting?.groups]); // Trigger when groups change

  // Real-time synchronization - poll for updates every 3 seconds
  useEffect(() => {
    if (!meeting || !isEditMode) return;

    const pollInterval = setInterval(async () => {
      try {
        const updatedMeeting = await fetchMeetingMinutesMeeting(id, meetingId);
        
        // Only update if there are actual changes (compare timestamps or content)
        // This prevents overwriting local unsaved changes
        if (updatedMeeting.updated_at !== meeting.updated_at) {
          // Merge updates carefully - don't overwrite local changes
          setMeeting((prevMeeting) => {
            // Only update if we don't have unsaved changes
            if (!hasUnsavedChanges) {
              meetingRef.current = updatedMeeting;
              return updatedMeeting;
            }
            // If we have unsaved changes, show a notification instead
            return prevMeeting;
          });
          
          if (hasUnsavedChanges) {
            toast.info(t("meeting_minutes.others_editing"), { duration: 3000 });
          }
        }
      } catch (err) {
        console.error("Error polling for updates:", err);
      }
    }, 3000); // Poll every 3 seconds

    return () => clearInterval(pollInterval);
  }, [id, meetingId, meeting, isEditMode, hasUnsavedChanges, t]);

  // Update local state immediately for responsive UI
  const updateEntryLocal = (groupId, entryId, field, value) => {
    setMeeting((prevMeeting) => {
      const updated = {
        ...prevMeeting,
        groups: prevMeeting.groups.map((g) =>
          g.id === groupId
            ? {
                ...g,
                entries: g.entries.map((e) =>
                  e.id === entryId ? { ...e, [field]: value } : e
                ),
              }
            : g
        ),
      };
      meetingRef.current = updated;
      checkForUnsavedChanges(updated);
      return updated;
    });
  };

  // Debounced API update
  const updateEntry = useCallback((groupId, entryId, field, value) => {
    // Update local state immediately
    updateEntryLocal(groupId, entryId, field, value);

    // Clear existing timer for this entry+field combination
    const timerKey = `entry-${groupId}-${entryId}-${field}`;
    const existingTimer = debounceTimers.current[timerKey];
    if (existingTimer) {
      clearTimeout(existingTimer);
      debounceTimers.current[timerKey] = null;
    }

    // Set new timer
    const timer = setTimeout(async () => {
      try {
        setSaveStatus('saving');
        const currentMeeting = meetingRef.current || { groups: [] };
        const group = currentMeeting.groups.find((g) => g.id === groupId);
        if (!group) {
          setSaveStatus(null);
          delete debounceTimers.current[timerKey];
          return;
        }
        const entry = group.entries.find((e) => e.id === entryId);
        if (!entry) {
          setSaveStatus(null);
          delete debounceTimers.current[timerKey];
          return;
        }

        const updatedEntry = await updateMeetingMinutesEntry(
          id,
          meetingId,
          groupId,
          entryId,
          {
            ...entry,
            [field]: value,
          }
        );

        setMeeting((prevMeeting) => {
          const updated = {
            ...prevMeeting,
            groups: prevMeeting.groups.map((g) =>
              g.id === groupId
                ? {
                    ...g,
                    entries: g.entries.map((e) =>
                      e.id === entryId ? updatedEntry : e
                    ),
                  }
                : g
            ),
          };
          meetingRef.current = updated;
          initialMeetingRef.current = JSON.parse(JSON.stringify(updated)); // Update initial state
          setHasUnsavedChanges(false);
          setSaveStatus('success');
          setTimeout(() => setSaveStatus(null), 3000);
          return updated;
        });
      } catch (err) {
        console.error("Error updating entry:", err);
        setSaveStatus('error');
        setSaveErrorMessage(t("meeting_minutes.entry.update_error"));
        setTimeout(() => {
          setSaveStatus(null);
          setSaveErrorMessage(null);
        }, 5000);
        toast.error(t("meeting_minutes.entry.update_error"));
      } finally {
        delete debounceTimers.current[timerKey];
      }
    }, 500); // 500ms debounce
    
    // Store timer reference
    debounceTimers.current[timerKey] = timer;
  }, [id, meetingId, t]);

  const removeEntry = async (groupId, entryId) => {
    if (!window.confirm(t("meeting_minutes.entry.delete_confirm"))) {
      return;
    }

    try {
      await deleteMeetingMinutesEntry(id, meetingId, groupId, entryId);
      setMeeting((prevMeeting) => {
        const updated = {
          ...prevMeeting,
          groups: prevMeeting.groups.map(g => 
            g.id === groupId
              ? { ...g, entries: g.entries.filter(e => e.id !== entryId) }
              : g
          ),
        };
        meetingRef.current = updated;
        return updated;
      });
      toast.success(t("meeting_minutes.entry.deleted"));
    } catch (err) {
      console.error("Error deleting entry:", err);
      toast.error(t("meeting_minutes.entry.delete_error"));
    }
  };

  if (loading) {
    return <ResourceLoading />;
  }

  if (error || !meeting) {
    return <ResourceError />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Save Bar */}
      <SaveBar
        hasChanges={hasUnsavedChanges}
        isSaving={saveStatus === 'saving'}
        isError={saveStatus === 'error'}
        isSuccess={saveStatus === 'success'}
        errorMessage={saveErrorMessage}
        successMessage={t("meeting_minutes.changes_saved")}
        onDismiss={() => {
          setSaveStatus(null);
          setSaveErrorMessage(null);
        }}
      />
      
      <DashboardHeader
        title={`${t("meeting_minutes.meeting_number")} ${meeting.meeting_number} - ${meetingMinutes?.title || ""}${(meetingMinutes?.project || meeting?.project) ? ` (${(meetingMinutes?.project || meeting?.project)?.displayTitle || (meetingMinutes?.project || meeting?.project)?.name || `Project #${(meetingMinutes?.project || meeting?.project)?.id}`})` : ""}`}
        actions={[
          {
            label: t("Back"),
            onClick: () => navigate(`/dashboard/meeting-minutes/${id}`),
            icon: ArrowLeftIcon,
          },
          {
            label: isEditMode ? t("View Mode") : t("Edit Mode"),
            onClick: () => setIsEditMode(!isEditMode),
            icon: isEditMode ? EyeIcon : PencilIcon,
          },
        ]}
      />

      <div className="max-w-6xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8">
        <div className="bg-white shadow-sm rounded-lg p-4 sm:p-6">
          {/* Header Section */}
          <div className="mb-4 sm:mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
              <div className="flex-1 min-w-0">
                <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-gray-900 truncate">
                  {t("meeting_minutes.meeting_number")} {meeting.meeting_number}
                </h2>
                <p className="text-xs sm:text-sm text-gray-500 mt-1">
                  {meeting.meeting_date}
                </p>
                {(meetingMinutes?.project || meeting?.project) && (
                  <div className="mt-2">
                    <a
                      href={`/dashboard/projects/${(meetingMinutes?.project || meeting?.project)?.id}`}
                      className="text-xs sm:text-sm text-indigo-600 hover:text-indigo-800 hover:underline inline-flex items-center gap-1"
                    >
                      <span className="font-medium">{t("meeting_minutes.project")}:</span>
                      <span>{(meetingMinutes?.project || meeting?.project)?.displayTitle || (meetingMinutes?.project || meeting?.project)?.name || `Project #${(meetingMinutes?.project || meeting?.project)?.id}`}</span>
                    </a>
                  </div>
                )}
              </div>
              <div className="flex-shrink-0">
                {!isFinished && (
                  <button
                    onClick={async () => {
                      try {
                        // Check if meeting minutes is finished - if so, prevent finishing meetings
                        // Note: Adjust this check based on your business logic
                        // For now, we allow finishing meetings regardless of other meetings being created
                        await updateMeetingMinutesMeeting(id, meetingId, { is_finished: true });
                        setIsFinished(true);
                        setMeeting((prevMeeting) => {
                          const updated = { ...prevMeeting, is_finished: true };
                          meetingRef.current = updated;
                          return updated;
                        });
                        toast.success(t("meeting_minutes.meeting.marked_finished"));
                      } catch (err) {
                        console.error("Error marking meeting as finished:", err);
                        const errorMessage = err?.response?.data?.errors?.[0] || err?.message || t("meeting_minutes.meeting.mark_finished_error");
                        toast.error(errorMessage);
                      }
                    }}
                    className="w-full sm:w-auto inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-transparent rounded-md shadow-sm text-xs sm:text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
                  >
                    <CheckIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2" />
                    <span className="hidden sm:inline">{t("meeting_minutes.meeting.mark_finished")}</span>
                    <span className="sm:hidden">{t("Ferdig")}</span>
                  </button>
                )}
                {isFinished && (
                  <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium bg-green-100 text-green-800">
                    {t("meeting_minutes.meeting.finished")}
                  </span>
                )}
              </div>
            </div>
          </div>


          {/* Add Point Button */}
          {!isFinished && isEditMode && (
            <div className="mb-4 sm:mb-6">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  addGroup(e);
                }}
                className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
              >
                <PlusIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                {t("meeting_minutes.add_point")}
              </button>
            </div>
          )}

          <div className="space-y-4 sm:space-y-6">
            {meeting.groups && meeting.groups.length > 0 ? (
              meeting.groups.map((group) => (
                <div key={group.id} data-group-id={group.id} className="border-l-4 border-indigo-500 pl-3 sm:pl-4 pr-2 sm:pr-0">
                  {/* Group Header */}
                  <div className="flex items-start justify-between gap-2 mb-2 sm:mb-3">
                    <div className="flex-1">
                      <h4 className="text-base sm:text-lg font-medium text-gray-900">
                        {group.title || t("meeting_minutes.point.untitled")}
                      </h4>
                    </div>
                    {!isFinished && isEditMode && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openGroupDialog(group)}
                          className="flex-shrink-0 text-indigo-600 hover:text-indigo-800 p-1 rounded-md hover:bg-indigo-50 transition-colors"
                          aria-label={t("Edit")}
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => removeGroup(group.id)}
                          className="flex-shrink-0 text-red-600 hover:text-red-800 p-1 rounded-md hover:bg-red-50 transition-colors"
                          aria-label={t("meeting_minutes.group.delete")}
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Group Description */}
                  {group.description && (
                    <p className="text-sm sm:text-base text-gray-700 mb-3 whitespace-pre-wrap">{group.description}</p>
                  )}

                  {/* Entries Section */}
                  <div className="ml-2 sm:ml-4 space-y-3">
                    {group.entries && group.entries.length > 0 && (
                      <div className="space-y-3">
                        {group.entries.map((entry) => (
                          <div key={entry.id} className="bg-gray-50 p-3 sm:p-4 rounded-lg border border-gray-200">
                            {isFinished || !isEditMode ? (
                              <div>
                                <p className="text-sm sm:text-base text-gray-900 whitespace-pre-wrap">{entry.description}</p>
                                {(entry.deadline || entry.responsible_user_id) && (
                                  <div className="mt-2 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-xs sm:text-sm text-gray-600">
                                    {entry.deadline && (
                                      <span className="flex items-center">
                                        <span className="font-medium mr-1">{t("meeting_minutes.entry.deadline")}:</span>
                                        {entry.deadline}
                                      </span>
                                    )}
                                    {entry.responsible_user_id && (
                                      <span className="flex items-center">
                                        <span className="font-medium mr-1">{t("meeting_minutes.entry.responsible")}:</span>
                                        {entry.responsible_user?.displayTitle || entry.responsible_user_id}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="space-y-3">
                                <textarea
                                  value={entry.description || ""}
                                  onChange={(e) => updateEntry(group.id, entry.id, "description", e.target.value)}
                                  placeholder={t("meeting_minutes.entry.description")}
                                  rows={2}
                                  className="w-full text-sm text-gray-700 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-y min-h-[60px]"
                                />
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                                  <input
                                    type="date"
                                    value={entry.deadline || ""}
                                    onChange={(e) => updateEntry(group.id, entry.id, "deadline", e.target.value)}
                                    placeholder={t("meeting_minutes.entry.deadline")}
                                    className="w-full text-xs sm:text-sm border border-gray-300 rounded-md px-2 sm:px-3 py-1.5 sm:py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                  />
                                  <div className="w-full">
                                    <PMCustomSelector
                                      open={false}
                                      title={t("meeting_minutes.entry.select_responsible")}
                                      multiple={false}
                                      field="displayTitle"
                                      field2="email"
                                      field3="mobile"
                                      fetchFunction={async () => {
                                        const response = await fetchUsers({
                                          filters: [{ name: "EMPLOYEE", value: true }],
                                          per_page: 100,
                                        });
                                        return response?.data?.data || response?.data || response || [];
                                      }}
                                      selectedItems={entry.responsible_user_id ? [entry.responsible_user_id] : []}
                                      onChange={(e) => {
                                        const selectedId = Array.isArray(e.target.value) ? e.target.value[0] : e.target.value;
                                        updateEntry(group.id, entry.id, "responsible_user_id", selectedId || null);
                                      }}
                                      values={{ responsible_user_id: entry.responsible_user_id }}
                                      setFieldValue={(name, value) => {
                                        updateEntry(group.id, entry.id, name, value);
                                      }}
                                    />
                                  </div>
                                </div>
                                <div className="flex justify-end">
                                  <button
                                    onClick={() => removeEntry(group.id, entry.id)}
                                    className="inline-flex items-center text-xs sm:text-sm text-red-600 hover:text-red-800 px-2 py-1 rounded-md hover:bg-red-50 transition-colors"
                                    aria-label={t("meeting_minutes.entry.remove")}
                                  >
                                    <XMarkIcon className="h-4 w-4 mr-1" />
                                    {t("meeting_minutes.entry.remove")}
                                  </button>
                                </div>
                              </div>
                            )}
                            {/* Documents for this entry */}
                            <div className="mt-3">
                              <DocumentManagerV6
                                module="meeting_minutes"
                                moduleParam={`${id}:group:${group.id}:entry:${entry.id}`}
                                mode="dropzone"
                                showUpload={!isFinished && isEditMode}
                                allowDelete={!isFinished && isEditMode}
                                allowEdit={!isFinished && isEditMode}
                                allowCopy={true}
                                allowMove={true}
                                disabled={isFinished || !isEditMode}
                                autoLoad={true}
                                onDocumentsChange={handleEntryDocumentsChange}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {!isFinished && isEditMode && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          addEntry(e, group.id);
                        }}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-xs sm:text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500 transition-colors"
                      >
                        <PlusIcon className="h-4 w-4 mr-1.5" />
                        {t("meeting_minutes.entry.add")}
                      </button>
                    )}

                    {/* Previous Meeting Entries Accordion */}
                    {previousMeeting && previousMeeting.groups && previousMeeting.groups.length > 0 && (() => {
                      // Find corresponding group from previous meeting by order (matching order field)
                      const currentGroupOrder = group.order !== undefined ? group.order : meeting.groups.findIndex(g => g.id === group.id);
                      const prevGroup = previousMeeting.groups.find(g => (g.order !== undefined ? g.order : previousMeeting.groups.findIndex(pg => pg.id === g.id)) === currentGroupOrder) || previousMeeting.groups[meeting.groups.findIndex(g => g.id === group.id)];
                      const hasPrevEntries = prevGroup && prevGroup.entries && prevGroup.entries.length > 0;
                      const accordionKey = `prev-entries-${group.id}`;
                      const isExpanded = expandedGroups[accordionKey] !== undefined ? expandedGroups[accordionKey] : false;
                      
                      if (!hasPrevEntries) return null;
                      
                      return (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <button
                            onClick={() => setExpandedGroups(prev => ({ ...prev, [accordionKey]: !prev[accordionKey] }))}
                            className="w-full flex items-center justify-between text-left text-xs sm:text-sm text-gray-600 hover:text-gray-800 px-2 py-2 rounded-md hover:bg-gray-50 transition-colors"
                            aria-label={isExpanded ? (t("meeting_minutes.collapse") || "Skjul") : (t("meeting_minutes.expand") || "Vis")}
                          >
                            <span className="font-medium">
                              {t("meeting_minutes.from_previous_meeting")} ({t("meeting_minutes.meeting_number")} {previousMeeting.meeting_number}): {prevGroup.entries.length} {prevGroup.entries.length === 1 ? (t("meeting_minutes.entry.singular") || "punkt") : (t("meeting_minutes.entry.plural") || "punkter")}
                            </span>
                            {isExpanded ? (
                              <ChevronUpIcon className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 ml-2" />
                            ) : (
                              <ChevronDownIcon className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 ml-2" />
                            )}
                          </button>
                          {isExpanded && (
                            <div className="mt-3 ml-2 sm:ml-4 space-y-2">
                              {prevGroup.entries.map((entry) => (
                                <div key={`prev-entry-${entry.id}`} className="bg-gray-100 p-3 sm:p-4 rounded-lg border border-gray-300 opacity-75">
                                  <p className="text-sm sm:text-base text-gray-700 whitespace-pre-wrap">{entry.description}</p>
                                  {(entry.deadline || entry.responsible_user_id) && (
                                    <div className="mt-2 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-xs sm:text-sm text-gray-600">
                                      {entry.deadline && (
                                        <span className="flex items-center">
                                          <span className="font-medium mr-1">{t("meeting_minutes.entry.deadline")}:</span>
                                          {entry.deadline}
                                        </span>
                                      )}
                                      {entry.responsible_user_id && (
                                        <span className="flex items-center">
                                          <span className="font-medium mr-1">{t("meeting_minutes.entry.responsible")}:</span>
                                          {entry.responsible_user?.displayTitle || entry.responsible_user_id}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12 sm:py-16">
                <p className="text-sm sm:text-base text-gray-500">
                  {isFinished 
                    ? t("meeting_minutes.meeting.no_points")
                    : t("meeting_minutes.meeting.no_points_add_first")
                  }
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Group Dialog */}
      <PMDialog
        open={groupDialogOpen}
        onClose={closeGroupDialog}
        title={editingGroup ? t("meeting_minutes.point.edit") : t("meeting_minutes.add_point")}
        size="lg"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("meeting_minutes.point.title")}
            </label>
            <input
              type="text"
              value={groupFormData.title}
              onChange={(e) => setGroupFormData({ ...groupFormData, title: e.target.value })}
              placeholder={t("meeting_minutes.point.title")}
              className="w-full text-sm sm:text-base border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("meeting_minutes.point.description")}
            </label>
            <textarea
              value={groupFormData.description}
              onChange={(e) => setGroupFormData({ ...groupFormData, description: e.target.value })}
              placeholder={t("meeting_minutes.point.description")}
              rows={4}
              className="w-full text-sm text-gray-700 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-y"
            />
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={closeGroupDialog}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              onClick={saveGroup}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? t("Saving...") : (editingGroup ? t("Update") : t("Create"))}
            </button>
          </div>
        </div>
      </PMDialog>
    </div>
  );
};

